import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, PieLabelRenderProps, Line, Scatter, Layer, ComposedChart } from 'recharts';
import { Button } from './ui/button';
import { Calculator } from 'lucide-react';
import { ROICalculator } from './ROICalculator';
import { Post, calculatePlatformSpending } from './shared/postsData';
import { ChartLoader, ChartWrapper } from './ChartWrapper';
import { TIER_COLORS } from './ui/utils';
import { NoData } from './ui/noData';
import { formatBoth, formatShort } from '../../../../../../components/tailwind-component/ui/utils';
import dayjs from 'dayjs';
import { usePdfMode, usePdfVariant, PdfDataTable } from './pdf/PdfModeContext';
import { PostActivationTimeline } from './PostActivationTimeline';

// Monthly Activation Chart (influencer tiers per month) is replaced by the Post Activation
// Timeline (posts per day by posted date). Old chart kept below behind this flag.
const SHOW_MONTHLY_ACTIVATION = false;

const TIER_ORDER = [
  "Nano",
  "Micro",
  "Micro Plus",
  "Macro",
  "Macro Plus",
  "Mega",
  "Mega Plus",
];
const colors = TIER_COLORS;
// Function to calculate spending by influencer tier
const calculateTierSpending = (posts: Post[], platform: string) => {
  const filteredPosts = platform === 'all'
    ? posts
    : posts.filter(p => p.platform === platform);

  // Distribute posts across tiers based on engagement rates
  const tierSpending = {
    Nano: 0,
    Micro: 0,
    'Micro Plus': 0,
    Macro: 0,
    'Macro Plus': 0,
    Mega: 0,
    'Mega Plus': 0
  };

  filteredPosts.forEach(post => {
    const cost = post.cost || 0;
    const er = post.er;

    // Assign tier based on engagement rate (this is a simple heuristic)
    if (er >= 10) {
      tierSpending.Nano += cost;
    } else if (er >= 8.5) {
      tierSpending.Micro += cost;
    } else if (er >= 7.5) {
      tierSpending['Micro Plus'] += cost;
    } else if (er >= 6.5) {
      tierSpending.Macro += cost;
    } else {
      tierSpending['Macro Plus'] += cost;
    }
  });

  return tierSpending;
};

interface InfluencerPieChartsProps {
  platform: string;
  posts: Post[];
  data?: any;
  loading?: boolean;
  // Shared (public) links must not expose cost/ROI -- hides the Calculator
  shared?: boolean;
}





// export function InfluencerPieCharts({ loading, platform, posts = [], data }: InfluencerPieChartsProps) {
export function InfluencerPieCharts({ loading, platform, posts = [], data, shared = false }: InfluencerPieChartsProps) {
  // PDF report: hover values printed as tables, no buttons, no animation
  const pdfMode = usePdfMode();
  const pdfVariant = usePdfVariant();
  // Mobile PDF: shorter charts so two cards fit on one phone-sized page
  const chartH = (h: number) => (pdfVariant === 'mobile' ? Math.round(h * 0.72) : h);
  // const getPlatformData = (spending: { [key: string]: number }) => [
  //   {
  //     name: 'Instagram',
  //     value: data && data.influencer_by_platform && data?.influencer_by_platform[1]?.influencer_count || 0,
  //     color: '#fbbf24',
  //     views: data && data.influencer_by_platform && data?.influencer_by_platform[1]?.total_play_count || 0,
  //     er: data && data.influencer_by_platform && data?.influencer_by_platform[1]?.er || 0,
  //     spent: spending['Instagram'] || 0
  //   },
  //   {
  //     name: 'YouTube',
  //     value: data && data.influencer_by_platform && data?.influencer_by_platform[2]?.influencer_count || 0,
  //     color: '#c084fc',
  //     views: data && data.influencer_by_platform && data?.influencer_by_platform[2]?.total_play_count || 0,
  //     er: data && data.influencer_by_platform && data?.influencer_by_platform[2]?.er || 0,
  //     spent: spending['YouTube'] || 0
  //   },
  //   {
  //     name: 'Facebook',
  //     value: data && data.influencer_by_platform && data?.influencer_by_platform[0]?.influencer_count || 0,
  //     color: '#93c5fd',
  //     views: data && data.influencer_by_platform && data?.influencer_by_platform[0]?.total_play_count || 0,
  //     er: data && data.influencer_by_platform && data?.influencer_by_platform[0]?.er || 0,
  //     spent: spending['Facebook'] || 0
  //   }
  // ];

  const getPlatformData = (spending: { [key: string]: number }) => {
    const platforms = [
      {
        name: 'Instagram',
        value: data?.influencer_by_platform?.[1]?.influencer_count ?? 0,
        color: '#fbbf24',
        views: data?.influencer_by_platform?.[1]?.total_play_count ?? 0,
        er: data?.influencer_by_platform?.[1]?.er ?? 0,
        spent: spending['Instagram'] ?? 0,
      },
      {
        name: 'YouTube',
        value: data?.influencer_by_platform?.[2]?.influencer_count ?? 0,
        color: '#c084fc',
        views: data?.influencer_by_platform?.[2]?.total_play_count ?? 0,
        er: data?.influencer_by_platform?.[2]?.er ?? 0,
        spent: spending['YouTube'] ?? 0,
      },
      {
        name: 'Facebook',
        value: data?.influencer_by_platform?.[0]?.influencer_count ?? 0,
        color: '#93c5fd',
        views: data?.influencer_by_platform?.[0]?.total_play_count ?? 0,
        er: data?.influencer_by_platform?.[0]?.er ?? 0,
        spent: spending['Facebook'] ?? 0,
      },
    ];

    // ❗ Remove platforms with 0 influencers
    return platforms.filter(platform => platform.value > 0);
  };
  const getInfluencerTypeData = (tierSpending: { [key: string]: number }) => {
    const list = data?.influencer_by_type ?? [];  // <-- SAFE DEFAULT
    const rows = [
      { name: 'Nano', value: list[0]?.influencer_count ?? 0, color: TIER_COLORS["Nano"], spent: list[0]?.influencer_cost || 0 },
      { name: 'Micro', value: list[1]?.influencer_count ?? 0, color: TIER_COLORS["Micro"], spent: list[1]?.influencer_cost || 0 },
      { name: 'Micro Plus', value: list[2]?.influencer_count ?? 0, color: TIER_COLORS["Micro Plus"], spent: list[2]?.influencer_cost || 0 },
      { name: 'Macro', value: list[3]?.influencer_count ?? 0, color: TIER_COLORS["Macro"], spent: list[3]?.influencer_cost || 0 },
      { name: 'Macro Plus', value: list[4]?.influencer_count ?? 0, color: TIER_COLORS["Macro Plus"], spent: list[4]?.influencer_cost || 0 },
      { name: 'Mega', value: list[5]?.influencer_count ?? 0, color: TIER_COLORS["Mega"], spent: list[5]?.influencer_cost || 0 },
      { name: 'Mega Plus', value: list[6]?.influencer_count ?? 0, color: TIER_COLORS["Mega Plus"], spent: list[6]?.influencer_cost || 0 },
    ];



    return rows.filter(item => item.value > 0);
  };


  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const row = payload[0].payload;

    return (
      <div className="bg-white px-3 py-3 rounded-lg shadow-md border border-gray-300 w-[250px] flex flex-col">
        <p className="text-slate-800 font-medium mb-1 d-block fs-14 pb-2">
          Influencers Activated
        </p>

        <div className="flex flex-wrap gap-x-2 gap-y-2">
          {[
            { name: "Nano", color: TIER_COLORS["Nano"] },
            { name: "Micro", color: TIER_COLORS["Micro"] },
            { name: "Micro Plus", color: TIER_COLORS["Micro Plus"] },
            { name: "Macro", color: TIER_COLORS["Macro"] },
            { name: "Macro Plus", color: TIER_COLORS["Macro Plus"] },
            { name: "Mega", color: TIER_COLORS["Mega"] },
            { name: "Mega Plus", color: TIER_COLORS["Mega Plus"] },
          ].map((item) => {
            const val = row[item.name];
            const er = row[`${item.name}_er`];

            if (val == null || val === 0) return null;

            return (
              <p
                key={item.name}
                style={{
                  color: "#2b2b2b",
                  fontSize: "13px",
                  display: "flex",
                  marginTop: "0px",
                }}
              >
                <span className="d-flex align-items-center">
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: item.color,
                      marginRight: "6px",
                    }}
                  ></span>
                  <span style={{ minWidth: "100px" }}>
                    {item.name}: {val}{" "}
                  </span>
                </span>
                {er != null && (
                  <span style={{ paddingLeft: 14, color: '#5d5d5d', fontSize: 11 }}>
                    (ER: {Number(er).toFixed(2)}%)
                  </span>
                )}
              </p>
            );
          })}
        </div>
      </div>
    );
  };

  const ERTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const row = payload[0].payload;

    return (
      <div className="bg-white px-3 py-3 rounded-lg shadow-md border border-gray-300 w-[220px]">
        <p className="text-slate-800 font-medium mb-2">{label}</p>

        {influencerBarKeys.map((tier) => {
          const er = row[`${tier}_er`];

          if (er === null || er === undefined || er === 0) return null;

          return (
            <div key={tier} className="flex items-center justify-between text-sm py-1">
              <span className="flex items-center gap-2">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: TIER_COLORS[tier],
                    borderRadius: 2,
                  }}
                ></span>
                {tier}
              </span>

              <span className="text-slate-700">{er.toFixed(2)}%</span>
            </div>
          );
        })}
      </div>
    );
  };


  // const CustomPlatformTooltip = ({ active, payload }: any) => {
  //   if (active && payload && payload.length) {
  //     const data = payload[0].payload;
  //     return (
  //       <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-slate-200">
  //         <p className="text-slate-900 mb-2">{data.name}</p>
  //         <p className="text-sm text-slate-600">{data.value} influencers</p>
  //         <p className="text-sm text-slate-600">{(data.views / 1000).toFixed(1)}K views</p>
  //         <p className="text-sm text-green-600">{data.er}% ER</p>
  //         {data.spent > 0 && (
  //           <p className="text-sm text-purple-600 font-medium pt-1 mt-1 border-t">
  //             Spent: ₹{data.spent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
  //           </p>
  //         )}
  //       </div>
  //     );
  //   }
  //   return null;
  // };

  const CustomPlatformTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload;

    return (
      <div className="bg-white px-3 py-3 rounded-md shadow-md border border-slate-200 min-w-[160px]">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <span style={{ display: 'flex', alignItems: 'center', width: '12px', height: '12px', backgroundColor: data.color, }}></span>
          {/* <span
          className="w-2.5 h-2.5 rounded-sm"
          style={{ backgroundColor: data.color }}
        /> */}
          <p className="text-slate-900 text-sm font-medium">
            {data.name}
          </p>
        </div>

        {/* Influencers */}

        <p className="text-sm text-slate-700 mb-1">
          {data.value} influencer{data.value > 1 ? 's' : ''}

        </p>

        <div className='d-flex'>
          <p className="text-sm text-slate-600">
            {/* {data.views} */}
            {formatShort(data.views)}
            {/* {(data.views / 1000).toFixed(1)}K  */}
            <span className='ml-1'>Views</span>

          </p>
          <p className="text-sm text-green-600 ml-4">{data.er}% ER</p>
        </div>
        {/* Amount Spent */}
        {data.spent > 0 && (
          <p className="text-sm mt-0.5" style={{ color: '#00aae5' }}>
            Amount Spent: ₹{data.spent.toLocaleString()}
          </p>
        )}

      </div>
    );
  };

  const influencerBarKeys = [
    "Nano",
    "Micro",
    "Micro Plus",
    "Macro",
    "Macro Plus",
    "Mega",
    "Mega Plus",
  ];

  const parseEr = (val: any) => {
    if (val == null) return null;
    if (typeof val === "string") {
      const m = val.match(/-?\d+(\.\d+)?/);
      return m ? parseFloat(m[0]) : null;
    }
    if (typeof val === "number") return val;
    return null;
  };

  const sourceData = Array.isArray(data?.insight_by_influencer_type)
    ? data.insight_by_influencer_type
    : [];

  const cleanedData = sourceData.map((row: any) => {
    const monthLabel =
      row.month ??
      (row.month_key ? row.month_key.split("-")[1] : null) ??
      "-";

    const newRow: any = { month: monthLabel };

    Object.entries(row).forEach(([key, val]: any) => {
      if (key !== "month" && key !== "month_key" && val && typeof val === "object") {
        newRow[key] = val.value ?? 0;
        // normalize ER into a numeric value (e.g. "7%" -> 7)
        const erNum = parseEr(val.er);
        newRow[`${key}_er`] = erNum != null ? erNum : null;
      }
    });

    return newRow;
  });


  const allErValues = cleanedData.flatMap((r: any) =>
    influencerBarKeys.map(k => r[`${k}_er`]).filter((v: any) => v != null)
  );

  let maxEr = allErValues.length ? Math.max(...allErValues) : 10;

  const maxEr1 = Math.max(
  ...cleanedData.flatMap((row: any) =>
    influencerBarKeys
      .map(k => Number(row[`${k}_er`]))
      .filter(v => !isNaN(v) && v > 0)
  ),
  0
);

  if (maxEr > 0 && maxEr <= 1) {
    // convert all stored ERs to percentages (mutate cleanedData entries)
    cleanedData.forEach((r: any) => {
      influencerBarKeys.forEach(k => {
        const key = `${k}_er`;
        if (r[key] != null) r[key] = Number(r[key]) * 100;
      });
    });
    // recompute
    const all = cleanedData.flatMap((r: any) =>
      influencerBarKeys.map(k => r[`${k}_er`]).filter((v: any) => v != null)
    );
    maxEr = all.length ? Math.max(...all) : maxEr * 100;
  }

  // ---------- ER AXIS CALCULATION ----------
const erMaxRaw = allErValues.length ? Math.max(...allErValues) : 0;

// If ER values are small (e.g. 2), add buffer (+5)
const erAxisMax =
  erMaxRaw <= 2
    ? 7
    : Math.ceil(erMaxRaw + 2);

// Aim for ~7 ticks
const erTickCount = 7;

// Generate ticks evenly
const erStep = Math.ceil(erAxisMax / (erTickCount - 1));
const erTicks = Array.from(
  { length: erTickCount },
  (_, i) => i * erStep
);
  // round up and create ticks array with ~5 steps
  const niceMax = Math.ceil(maxEr + 1); // buffer
  const step = Math.ceil(niceMax / 5) || 1;
  const rightTicks = Array.from({ length: 6 }, (_, i) => i * step); // [0, step, 2*step ...]

  const CustomTypeTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-slate-200">
          <span className='d-flex align-items-center'>
            <span style={{ display: 'flex', alignItems: 'center', width: '12px', height: '12px', backgroundColor: data.color, marginRight: '6px' }}></span>
            <p className="text-slate-900" style={{ fontSize: '13px' }}>{data.name}</p>
          </span>
          <p className="text-sm text-slate-600">{data.value} influencers</p>
          {/* {data.spent > 0 && ( */}
          <p className="text-sm  font-medium pt-1 mt-1 border-t" style={{ color: '#00aae5' }}>
            Amount Spent: {data.spent && data.spent > 0 ? "₹" : ""} {data.spent && data.spent > 0
              ? data.spent.toLocaleString(undefined)
              : "--"}
          </p>
          {/* )} */}
        </div>
      );
    }
    return null;
  };



  const [showROIPlatform, setShowROIPlatform] = useState(false);
  const [showROIType, setShowROIType] = useState(false);
  const platformSpending = calculatePlatformSpending(posts);
  const platformData = getPlatformData(platformSpending);
  const tierSpending = calculateTierSpending(posts, platform);
  const [dynamicBarSize, setDynamicBarSize] = useState(20);
  const influencerTypeData = getInfluencerTypeData(
    tierSpending,  // 👈 FIX
  );
  const isAllPlatforms = platform === 'all';
  const scrollRef = useRef<HTMLDivElement>(null);
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // === Sticky Y-Axis Logic ===
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const handleScroll = () => {
      const stickyAxis = document.querySelectorAll(".sticky-y-axis");

      stickyAxis.forEach((el: any) => {
        el.style.transform = `translateX(${scrollEl.scrollLeft}px)`;
      });
    };

    scrollEl.addEventListener("scroll", handleScroll);
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;

    const calculateSizes = () => {
      const containerWidth = scrollRef.current!.clientWidth; // visible area
      const months = cleanedData.length;
      const totalBarsPerMonth = influencerBarKeys.length;

      // full width dedicated to one month
      const monthBlockWidth = containerWidth / months;

      // bar size inside one month block
      const barSize =
        (monthBlockWidth * 0.7) / totalBarsPerMonth; // 70% occupied by bars

      const finalSize = Math.max(8, Math.min(60, barSize));

      setDynamicBarSize(finalSize);
    };

    calculateSizes();

    window.addEventListener("resize", calculateSizes);
    return () => window.removeEventListener("resize", calculateSizes);
  }, [cleanedData]);

  const maxCount = Math.max(
    ...cleanedData.flatMap((row: any) =>
      influencerBarKeys.map(k => Number(row[k] || 0))
    ),
    0
  );

  // pad even if data is small (all 1s)
const roundUpTo = (value: number, step: number) =>
  Math.ceil(value / step) * step;

// Decide bucket size dynamically
let influencerAxisMax = 10;

if (maxCount <= 5) {
  influencerAxisMax = 10;
} else if (maxCount <= 20) {
  influencerAxisMax = 20;
} else if (maxCount <= 50) {
  influencerAxisMax = 50;
} else {
  influencerAxisMax = roundUpTo(maxCount, 150);
}

const influencerTicks = Array.from(
  { length: influencerAxisMax / 5 + 10 },
  (_, i) => i * 5
);
const yMax =
  maxCount <= 5
    ? 10
    : roundUpTo(maxCount, 10);

  // create integer ticks: 0, 1, 2, ..., yMax
  const yTicks = Array.from(
  { length: yMax / 5 + 1 },
  (_, i) => i * 5
);
  const [showER, setShowER] = useState(false);
  // PDF tables: only tiers that have influencers
  const monthlyTiers = (rows: any[]) =>
    influencerBarKeys.filter((k) => rows.some((r: any) => Number(r?.[k]) > 0));
  if (!isAllPlatforms) {
    return (
      <>
        {/* Influencers by Type */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Influencers by Type</CardTitle>
                <CardDescription>Distribution by influencer tier</CardDescription>
              </div>
              {!shared && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowROIType(!showROIType)}
                  className={showROIType ? 'bg-purple-50 border-purple-300' : ''}
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {showROIType ? 'Chart' : 'Calculator'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {(showROIType && !shared) ? (
              <ROICalculator platform={platform} posts={posts} />
            ) : (
              <>
                {influencerTypeData.length > 0 && (
                  <ChartWrapper loading={loading} type="pie" height={chartH(280)}>
                    <>
                      <ResponsiveContainer width="100%" height={chartH(260)}> 
                        <PieChart>
                          <Pie
                            isAnimationActive={!pdfMode}
                            data={influencerTypeData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}`}
                            outerRadius={65}
                            fill="#8884d8"
                            dataKey="value"
                            style={{ fontSize: '13px' }}
                          >
                            {influencerTypeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTypeTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>

                    </>
                  </ChartWrapper>
                )}
                {influencerTypeData.length === 0 && !loading && (
                  <NoData message='No influencer data available for the selected range.' />
                )}
              </>
            )}
            {pdfMode && (
              <PdfDataTable
                columns={[{ label: 'Tier' }, { label: 'Influencers' }]}
                rows={influencerTypeData.map((t) => [t.name, t.value])}
                swatches={influencerTypeData.map((t) => t.color)}
              />
            )}
          </CardContent>
        </Card>

        {/* Post Activation Timeline - replaces Monthly Activation Chart */}
        <PostActivationTimeline
          rows={data?.post_activation_timeline}
          platform={platform}
          loading={loading}
          height={chartH(280)}
        />

        {/* OLD: Insights by Influencer Type (Monthly Activation Chart) */}
        {SHOW_MONTHLY_ACTIVATION && (
        <Card>
          <CardHeader>
            <div className="flex justify-between">
              <div className='mr-2'>
                <CardTitle>Monthly Activation Chart</CardTitle>
                <CardDescription>Distribution and performance by influencer category</CardDescription>
              </div>
              {!pdfMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowER(!showER)}
                className={showER ? "bg-purple-50 border-purple-300" : ""}
              >
                {showER ? "Show Influencer" : "Show ER"}
              </Button>
              )}
            </div>

          </CardHeader>
          <CardContent>
            <div
              ref={scrollRef}
              className="overflow-x-auto overflow-y-hidden w-full"
              style={{ paddingBottom: 10 }}
            >
              <div ref={chartWrapperRef} style={{ width: "100%" }}>
                {loading ? (
                  <ChartLoader height={280} />
                ) : cleanedData.length === 0 ? (
                  <NoData message="No influencer data available for the selected range." />
                ) : (
                  <>
                    {showER ? (
                      /* ⭐ ER CHART (Dots or Lines) */
                      <ResponsiveContainer width="100%" height={chartH(280)}>
                        <BarChart data={cleanedData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month" />
                          {/* <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 13 }}
                            domain={[0, yMax]}
                            ticks={yTicks}
                          /> */}
                          <YAxis
                            yAxisId="left"
                            domain={showER ? [0, erAxisMax] : [0, influencerAxisMax]}
                            ticks={showER ? erTicks : influencerTicks}
                            tick={{ fontSize: 13 }}
                            tickFormatter={showER ? (v) => `${v}%` : undefined}
                          />
                          <Tooltip content={<ERTooltip />} />

                          {/* ⭐ ER BARS — this is the missing part */}
                          {influencerBarKeys.map((tier) => {
                            const erKey = `${tier}_er`;

                            // Check if ANY month has value > 0 for this tier
                            const hasData = cleanedData.some((item: any) => Number(item[erKey]) > 0);

                            if (!hasData) return null; // ⛔ nothing to render → no space taken

                            return (
                              <Bar
                                isAnimationActive={!pdfMode}
                                key={erKey}
                                dataKey={erKey}
                                yAxisId="left"
                                barSize={dynamicBarSize}
                                fill={colors[tier]}
                                radius={[4, 4, 0, 0]}
                              />
                            );
                          })}
                        </BarChart>
                      </ResponsiveContainer>

                    ) : (
                      /* ⭐ INFLUENCER COUNT BAR CHART */
                      <ResponsiveContainer width="100%" height={chartH(280)}>
                        <BarChart data={cleanedData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month" />
                          <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 13 }}
                            domain={[0, yMax]}
                            ticks={yTicks}
                          />
                          <Tooltip content={<CustomTooltip />} />

                          {influencerBarKeys.map((key) => {
                            // Check if ANY month has value > 0 for this tier
                            const hasData = cleanedData.some((item: any) => Number(item[key]) > 0);

                            if (!hasData) return null; // ⛔ nothing to render → no space taken
                            return <Bar
                              isAnimationActive={!pdfMode}
                              key={key}
                              dataKey={key}
                              yAxisId="left"         // ⭐ required
                              barSize={dynamicBarSize}
                              fill={colors[key]}
                              shape={(props: any) => {
                                const { x, y, width, height, payload } = props;
                                const value = payload[key];
                                if (value == null) return <g />;
                                return (
                                  <rect
                                    x={x}
                                    y={y}
                                    width={width}
                                    height={height}
                                    fill={colors[key]}
                                    rx={4}
                                  />
                                );
                              }}
                            />
                          }
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </>
                )}
              </div>
            </div>
            {pdfMode && (
              <PdfDataTable
                columns={[{ label: 'Month' }, ...monthlyTiers(cleanedData).map((t) => ({ label: t }))]}
                rows={cleanedData.map((row: any) => [
                  row.month,
                  ...monthlyTiers(cleanedData).map((t) =>
                    row[t] ? `${row[t]}${row[`${t}_er`] != null ? ` (ER ${Number(row[`${t}_er`]).toFixed(2)}%)` : ''}` : '-'
                  ),
                ])}
              />
            )}
          </CardContent>
        </Card>
        )}
      </>
    );
  }
  // const transformInfluencerTypeData = (rawData: any[]) => {
  //   const grouped: Record<string, any> = {};

  //   rawData.forEach(item => {
  //     const month = item.month_key;

  //     if (!grouped[month]) {
  //       grouped[month] = {
  //         month,
  //         Macro: 0,
  //         'Macro Plus': 0,
  //         Mega: 0,
  //         'Mega Plus': 0,
  //         Micro: 0,
  //         Nano: 0,
  //       };
  //     }

  //     grouped[month][item.influencer_type] = item.influencer_count;
  //   });

  //   return Object.values(grouped);
  // };
  
  const transformInfluencerTypeData = (rawData: any[]) => {
  const grouped: Record<string, any> = {};

  rawData.forEach(item => {
    const month = dayjs(item.month_key, "YYYY-MM").format("MMM 'YY");

    if (!grouped[month]) {
      grouped[month] = {
        month,
        Macro: 0,
        'Macro Plus': 0,
        Mega: 0,
        'Mega Plus': 0,
        Micro: 0,
        Nano: 0,
      };
    }

    grouped[month][item.influencer_type] = item.influencer_count;
  });

  return Object.values(grouped);
};
  const chartData = transformInfluencerTypeData(
    data?.insight_by_influencer_type || []
  );
  // For "All Platforms", return the grid with all 3 cards
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Influencers by Platform - Only for "All Platforms" */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Influencers by Platform</CardTitle>
              <CardDescription>Distribution across social platforms</CardDescription>
            </div>
            {!shared && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowROIPlatform(!showROIPlatform)}
                className={showROIPlatform ? 'bg-purple-50 border-purple-300' : ''}
              >
                <Calculator className="h-4 w-4 mr-2" />
                {showROIPlatform ? 'Chart' : 'Calculator'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {(!loading && platformData.length === 0) && (
            <NoData message="No post type data available for the selected range" />
          )}
          {(showROIPlatform && !shared) ? (
            <ROICalculator platform={platform} posts={posts} />
          ) : (
            <>
             {loading && (
            <ChartLoader height={280} />
          )}
          {!loading && platformData && (
           <ChartWrapper type="pie" height={chartH(200)}>
              <PieChart>
                <Pie
                  isAnimationActive={!pdfMode}
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {platformData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} style={{ fontSize: 13 }} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPlatformTooltip />} />
              </PieChart>
            </ChartWrapper>
          )}
          
            </>
           
          )}
          {pdfMode && (
            <PdfDataTable
              columns={[{ label: 'Platform' }, { label: 'Influencers' }, { label: 'Views' }, { label: 'ER' }]}
              rows={platformData.map((d) => [d.name, d.value, Number(d.views || 0).toLocaleString('en-IN'), `${d.er}%`])}
              swatches={platformData.map((d) => d.color)}
            />
          )}
        </CardContent>
      </Card>

      {/* Influencers by Type */}
      <Card>
        <CardHeader>
          <CardTitle>Influencers by Type</CardTitle>
          <CardDescription>Distribution by influencer tier</CardDescription>
        </CardHeader>
        <CardContent>
          {influencerTypeData.length === 0 && !loading && (
            <NoData message='No influencer data available for the selected range.' />
          )}
          {loading ? <ChartLoader height={200} /> :
            <ResponsiveContainer width="100%" height={chartH(260)}>  
              <PieChart margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
                <Pie
                  isAnimationActive={!pdfMode}
                  data={influencerTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={65}

                  dataKey="value"
                  style={{ fontSize: 13 }}
                >
                  {influencerTypeData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                    />
                  ))}
                  {/* {influencerTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))} */}
                </Pie>
                <Tooltip content={<CustomTypeTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          }
          
          {pdfMode && (
            <PdfDataTable
              columns={[{ label: 'Tier' }, { label: 'Influencers' }]}
              rows={influencerTypeData.map((t) => [t.name, t.value])}
              swatches={influencerTypeData.map((t) => t.color)}
            />
          )}
        </CardContent>
      </Card>

      {/* Post Activation Timeline - replaces Monthly Activation Chart */}
      <PostActivationTimeline
        rows={data?.post_activation_timeline}
        platform={platform}
        loading={loading}
        height={chartH(300)}
      />

      {/* OLD: Insights by Influencer Type (Monthly Activation Chart) */}
      {SHOW_MONTHLY_ACTIVATION && (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Activation Chart</CardTitle>
          <CardDescription>Distribution by influencer category</CardDescription>
        </CardHeader>
        <CardContent>
          {(data?.insight_by_influencer_type?.length === 0) && !loading && (
            <NoData message='No influencer data available for the selected range.' />
          )}
          {data?.insight_by_influencer_type?.length > 0 && (
            <ChartWrapper type='pie'>
              <ResponsiveContainer width="100%" height={chartH(300)}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 13 }}
                    tickLine={false}
                    axisLine={false}
                  />

                  <YAxis
                    tick={{ fontSize: 13 }}
                    tickLine={false}
                    axisLine={false}
                  />

                  <Tooltip content={<CustomTooltip />} />
                  {/* <Legend /> */}
                  {TIER_ORDER.map(tier => (
                    <Bar
                      isAnimationActive={!pdfMode}
                      key={tier}
                      dataKey={tier}
                      fill={TIER_COLORS[tier]}
                      barSize={35}
                    />
                  ))}
                  {/* <Bar dataKey="Macro" fill="#fbbf24" barSize={35} />
                  <Bar dataKey="Macro Plus" fill="#fde68a" barSize={35} />
                  <Bar dataKey="Mega" fill="#f87171" barSize={35} />
                  <Bar dataKey="Mega Plus" fill="#ef4444" barSize={35} />
                  <Bar dataKey="Micro" fill="#c084fc" barSize={35} />
                  <Bar dataKey="Nano" fill="#60a5fa" barSize={35} /> */}
                </BarChart>
              </ResponsiveContainer>
              {/* <ResponsiveContainer width="100%" height={chartH(300)}>
                <BarChart
                  data={data?.insight_by_influencer_type}
                  margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 13 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 13 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="Macro" fill="#fbbf24" barSize={35} />
                  <Bar dataKey="Mega" fill="#f87171" barSize={35} />
                  <Bar dataKey="Micro" fill="#c084fc" barSize={35} />
                  <Bar dataKey="Nano" fill="#60a5fa" barSize={35} />
                </BarChart>


              </ResponsiveContainer> */}
            </ChartWrapper>
          )}
          
          {pdfMode && (
            <PdfDataTable
              columns={[{ label: 'Month' }, ...monthlyTiers(chartData).map((t) => ({ label: t }))]}
              rows={chartData.map((row: any) => [row.month, ...monthlyTiers(chartData).map((t) => row[t] || '-')])}
              swatches={undefined}
            />
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
